import React, { useMemo, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { TIER_LABEL, TIER_HINT, TIER_STYLE } from './badges';
import { UniverseRow } from './UniverseRow';

export const SummaryStrip = ({ summary, scanned, refinement }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="discovery-summary">
    <p className="text-sm font-semibold text-slate-900">
      {summary.companies} companies identified <span className="text-slate-300">|</span> {summary.product} product opportunities <span className="text-slate-300">|</span> {summary.platform} platform opportunities <span className="text-slate-300">|</span> {summary.manufacturing} with confirmed manufacturing capability
    </p>
    <p className="text-[11px] text-slate-500 mt-0.5">
      {summary.high} high · {summary.potential} potential · {summary.adjacent} adjacent{scanned ? ` · ${scanned} records scanned` : ''} · {summary.contacted} already contacted · {summary.dnc} do-not-contact
      {refinement && <span className="ml-1 text-sky-700 font-medium">· {refinement}</span>}
    </p>
  </div>
);

export const UniverseTable = ({ results, selected, onToggle, onSelectMany, navigate, research, researching, onResearch, crmContacts, onLoadContacts, onExport, exporting }) => {
  const [tierFilter, setTierFilter] = useState(null);
  const [q, setQ] = useState('');
  const [hideContacted, setHideContacted] = useState(false);

  const filtered = useMemo(() => {
    let rows = results || [];
    if (tierFilter) rows = rows.filter(r => r.tier === tierFilter);
    if (hideContacted) rows = rows.filter(r => !(r.crm?.contacted));
    if (q.trim()) {
      const s = q.toLowerCase();
      rows = rows.filter(r => `${r.company_name} ${r.country} ${r.rationale} ${(r.relevant_items || []).map(i => i?.name).join(' ')}`.toLowerCase().includes(s));
    }
    const order = { High: 0, Potential: 1, Adjacent: 2 };
    const rel = { Confirmed: 0, Probable: 1, Adjacent: 2 };
    return [...rows].sort((a, b) => (order[a.tier] - order[b.tier]) || (rel[a.relevance] - rel[b.relevance]) || (a.company_name || '').localeCompare(b.company_name || ''));
  }, [results, tierFilter, q, hideContacted]);

  const tiers = ['High', 'Potential', 'Adjacent'];
  const grouped = tiers.map(t => [t, filtered.filter(r => r.tier === t)]).filter(([, rows]) => rows.length);
  const selectableKeys = filtered.filter(r => !r.crm?.dnc).map(r => r.key);
  const allSelected = selectableKeys.length > 0 && selectableKeys.every(k => selected.has(k));

  return (
    <div className="space-y-2" data-testid="universe-table">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          <button onClick={() => setTierFilter(null)} className={`text-[11px] px-2.5 py-1 rounded-full border ${!tierFilter ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 text-slate-600'}`} data-testid="tier-filter-all">All ({(results || []).length})</button>
          {tiers.map(t => (
            <button key={t} onClick={() => setTierFilter(tierFilter === t ? null : t)} className={`text-[11px] px-2.5 py-1 rounded-full border ${tierFilter === t ? 'ring-2 ring-slate-900 ' : ''}${TIER_STYLE[t]}`} data-testid={`tier-filter-${t}`}>
              {t} ({(results || []).filter(r => r.tier === t).length})
            </button>
          ))}
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search companies…" className="text-xs border border-slate-200 rounded-full px-3 py-1 w-48" data-testid="universe-filter-input" />
        <label className="text-[11px] text-slate-600 inline-flex items-center gap-1"><input type="checkbox" checked={hideContacted} onChange={e => setHideContacted(e.target.checked)} data-testid="hide-contacted" /> Hide contacted</label>
        <label className="text-[11px] text-slate-600 inline-flex items-center gap-1"><input type="checkbox" checked={allSelected} onChange={() => onSelectMany(selectableKeys, !allSelected)} data-testid="select-all-visible" /> Select all ({selectableKeys.length})</label>
        <button onClick={onExport} disabled={exporting} className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="download-excel-btn" title="Excel with companies, evidence and BD contact emails">
          {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download Excel (companies + emails)
        </button>
      </div>

      {grouped.length === 0 && <p className="text-xs text-slate-500 py-4 text-center">No companies match the current filters.</p>}

      {grouped.map(([tier, rows]) => (
        <div key={tier} className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`tier-section-${tier}`}>
          <div className={`px-3 py-1.5 flex items-center gap-2 ${TIER_STYLE[tier]}`}>
            <span className="text-xs font-bold">{TIER_LABEL[tier]}</span>
            <span className="text-[11px] opacity-80 hidden md:inline">— {TIER_HINT[tier]}</span>
            <span className="ml-auto text-[11px] font-semibold">{rows.length}</span>
          </div>
          {rows.map(r => (
            <UniverseRow key={r.key} r={r} selected={selected.has(r.key)} onToggle={onToggle} navigate={navigate}
              research={research?.[r.key.replace(/\./g, '_')]} researching={researching} onResearch={onResearch}
              crmContacts={crmContacts?.[r.key]} onLoadContacts={onLoadContacts} />
          ))}
        </div>
      ))}
    </div>
  );
};
